import { useMemo } from 'react';
import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';
import type { ApiErrorDetail, ImportDetail } from '@klareospace/pharos-lib-types';
import { useParams } from '@tanstack/react-router';

import { flattenImportLineErrors } from '../utils';
import type { RejectedRow } from '../utils';
import { useImport } from './use-import';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseImportReportPageReturn {
  error: ApiErrorDetail | null;
  hiddenErrors: number;
  importDoc: ImportDetail | undefined;
  isLoading: boolean;
  rows: RejectedRow[];
  totalErrors: number;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Composes the import report page's data wiring — mirrors `useImportHistoryPage`. */
export function useImportReportPage(): UseImportReportPageReturn {
  const { importId } = useParams({ from: '/_authenticated/imports/history_/$importId' });
  const { data: importDoc, error, isLoading } = useImport(importId);

  const rows = useMemo<RejectedRow[]>(() => (importDoc ? flattenImportLineErrors(importDoc) : []), [importDoc]);
  const totalErrors = importDoc?.totalLineErrors ?? 0;
  const hiddenErrors = Math.max(0, totalErrors - rows.length);

  return {
    error: error ? getApiErrorDetail(error) : null,
    hiddenErrors,
    importDoc,
    isLoading,
    rows,
    totalErrors,
  };
}
